import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, ArrowRight, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { instituteHref, isExternalUrl } from "@/lib/instituteUrl";

/**
 * Top-bar institute switcher used on every public institute site.
 * Reads `/api/public/institutes` and routes to /<website_slug>.
 *
 * Props:
 *   - currentSlug: current site slug (omitted from list to avoid self-link)
 *   - theme: "dark" (default, for navy/ink utility bars) or "light" (for cream bars)
 *   - label: text displayed before the chevron — defaults to "Visit Institute"
 *   - testidPrefix: data-testid prefix — e.g. "poly-top-switcher" / "bul-top-switcher"
 */
export default function InstituteSwitcher({
  currentSlug = "",
  theme = "dark",
  label = "Visit Institute",
  testidPrefix = "switch",
}) {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/public/institutes");
        if (mounted) {
          setList(Array.isArray(data) ? data : []);
          setLoaded(true);
        }
      } catch {
        if (mounted) setLoaded(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const others = list.filter((i) => i.website_slug && i.website_slug !== currentSlug);
  if (loaded && others.length === 0) return null;

  const dark = theme !== "light";
  const triggerCls = dark
    ? "inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-white/80 hover:text-white"
    : "inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase text-[#1F2937] hover:text-[#AF734A]";

  return (
    <div ref={ref} className="relative" data-testid={`${testidPrefix}-root`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={triggerCls}
        data-testid={`${testidPrefix}-trigger`}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {label}
        <ChevronDown size={11} strokeWidth={2.4} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-1.5 z-50 w-72 max-h-[420px] overflow-y-auto bg-white text-[#1F2937] shadow-xl ring-1 ring-black/10 py-1"
          data-testid={`${testidPrefix}-menu`}
        >
          <div className="px-3 py-2 text-[10px] tracking-[0.25em] uppercase text-slate-500 border-b border-slate-100">
            GS Mandal Institutes
          </div>
          {others.map((it) => {
            const external = isExternalUrl(it.website_slug);
            return (
            <a
              key={it.id}
              href={instituteHref(it.website_slug)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`${testidPrefix}-item-${it.website_slug}`}
              className="w-full text-left px-3 py-2 flex items-center gap-3 hover:bg-slate-50 focus:bg-slate-50 outline-none no-underline text-inherit"
            >
              <div className="w-8 h-8 bg-slate-100 flex items-center justify-center shrink-0 rounded-sm overflow-hidden">
                {it.logo_url ? (
                  <img src={it.logo_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <span className="text-[10px] font-bold text-slate-400">
                    {(it.name || "?").slice(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[12px] font-semibold leading-tight truncate">{it.name}</div>
                <div className="text-[10px] text-slate-500 truncate">
                  {it.city || it.type || it.website_slug}
                  {it.year_of_establishment ? ` (${it.year_of_establishment})` : ""}
                </div>
              </div>
              {external ? (
                <ExternalLink size={12} strokeWidth={2} className="text-slate-400 shrink-0" />
              ) : (
                <ArrowRight size={12} strokeWidth={2} className="text-slate-300 shrink-0" />
              )}
            </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
